// src/services/purchaseRequestService.js
// D-304 B (Stabilization P2) — the ONLY writer of purchase_requests.
//
// WHY THIS FILE EXISTS
// The Procurement Queue used to write PRs and MRF line marks with a batch
// built from the page's OLD snapshot of each MRF:
//   - batching 2+ lines of one MRF marked only the LAST line (each update
//     rewrote the whole items[] from the same stale copy) → the others were
//     bought again;
//   - two managers / two tabs could batch the same need → double purchase;
//   - every MRF line got the FIRST PR's id, even when it went to another vendor.
// Now one transaction re-reads every MRF it touches and refuses if any of
// them changed since the queue was loaded ("reload and check again").
//
// The queue itself no longer trusts these marks (it calculates the need
// live — src/utils/procurementNeed.js); they are kept for traceability
// ("this line was put on PR-xx").

import {
  collection,
  doc,
  getDoc,
  getCountFromServer,
  query,
  where,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { financialYearOf, roundQty } from "../utils/procurementNeed";

const OPEN_MRF_STATES = new Set(["approved", "partially_issued"]);
const MANAGER_ROLES   = new Set(["owner", "sm", "manager"]);

const millisOf = (ts) =>
  ts && typeof ts.toMillis === "function" ? ts.toMillis() : null;

/**
 * Creates one PR per vendor, and marks the MRF lines each PR is for — all
 * in ONE transaction.
 *
 * groups: [{ vendor_id, vendor_name, rows: [{
 *     item_id, item_code, item_name, specification, unit, category,
 *     qty,                       // quantity to order
 *     sources: [{ mrf_id, mrf_no, item_index, site_id, site_name, qty }]
 *   }] }]
 * mrfVersions: { [mrf_id]: updated_at millis as loaded by the queue (or null) }
 *
 * Returns [{ id, purchase_request_no, vendor_name }].
 */
export const createPurchaseRequests = async ({
  company_id,
  groups,
  mrfVersions = {},
  created_by,
  created_by_name,
}) => {
  if (!company_id) throw new Error("company_id required");
  if (!created_by) throw new Error("Your login could not be read. Please log in again.");
  if (!Array.isArray(groups) || groups.length === 0) throw new Error("Select items first");

  for (const g of groups) {
    if (!g.vendor_id) throw new Error("Every item needs a vendor");
    if (!Array.isArray(g.rows) || g.rows.length === 0) throw new Error("Empty vendor group");
    for (const r of g.rows) {
      if (!String(r.item_id || "").trim()) throw new Error("Item without item_id");
      if (!(Number(r.qty) > 0)) throw new Error(`Quantity must be more than 0 for ${r.item_name || r.item_id}`);
    }
  }

  // MRFs to re-check = every MRF a source line points at.
  const mrfIds = [...new Set(
    groups.flatMap((g) => g.rows.flatMap((r) => (r.sources || []).map((s) => s.mrf_id)))
  )].filter(Boolean);

  // PR numbering: per company, atomic. First ever PR for a company seeds the
  // counter from the number of PRs it already has.
  const counterRef = doc(db, "system_counters", `pr_${company_id}`);
  let seed = 0;
  const counterPre = await getDoc(counterRef);
  if (!counterPre.exists()) {
    const c = await getCountFromServer(query(
      collection(db, "purchase_requests"),
      where("company_id", "==", company_id)
    ));
    seed = c.data().count;
  }

  const fy        = financialYearOf(new Date());
  const batchId   = `PB-${Date.now()}`;
  const now       = Timestamp.now(); // serverTimestamp() is not allowed inside arrays
  const prRefs    = groups.map(() => doc(collection(db, "purchase_requests")));

  return runTransaction(db, async (transaction) => {
    // ── reads first ──
    const counterSnap = await transaction.get(counterRef);
    const mrfSnaps = {};
    for (const id of mrfIds) {
      const snap = await transaction.get(doc(db, "material_requests", id));
      if (!snap.exists()) throw new Error("A material request was deleted. Please reload the queue.");
      const d = snap.data();
      if (d.company_id !== company_id) throw new Error("Access denied — material request of another company");
      const mrfLabel = d.mrf_no ? `MRF ${d.mrf_no}` : `The material request for ${d.site_name || "a site"}`;
      if (!OPEN_MRF_STATES.has(String(d.status || ""))) {
        throw new Error(`${mrfLabel} is no longer open. Please reload the queue.`);
      }
      const loaded = Object.prototype.hasOwnProperty.call(mrfVersions, id) ? mrfVersions[id] : undefined;
      if (loaded !== undefined && millisOf(d.updated_at) !== loaded) {
        throw new Error(
          `${mrfLabel} was changed after you opened the queue (another user may have ordered it). ` +
          `Nothing was saved — please reload and check again.`
        );
      }
      mrfSnaps[id] = d;
    }

    let last = counterSnap.exists() ? Number(counterSnap.data().last_number || 0) : seed;

    // ── writes ──
    const created = [];
    const marks = {}; // mrf_id → { item_index → { pr_id, pr_no } }

    groups.forEach((g, gi) => {
      last += 1;
      const prNo  = `PR-${fy}-${String(last).padStart(6, "0")}`;
      const prRef = prRefs[gi];

      const items = g.rows.map((r) => ({
        item_id:       String(r.item_id).trim(),
        item_code:     r.item_code || "",
        item_name:     r.item_name || "",
        specification: r.specification || "",
        unit:          r.unit || "nos",
        category:      r.category || "",
        qty:           roundQty(r.qty),
        order_qty:     roundQty(r.qty),
        is_custom_item: false,
        sources:       (r.sources || []).map((s) => ({
          mrf_id:     s.mrf_id,
          mrf_no:     s.mrf_no || "",
          item_index: Number(s.item_index),
          site_id:    s.site_id || "",
          site_name:  s.site_name || "",
          qty:        roundQty(s.qty),
        })),
      }));

      const sites = [...new Set(items.flatMap((i) => i.sources.map((s) => s.site_name)).filter(Boolean))];
      const mrfs  = [...new Set(items.flatMap((i) => i.sources.map((s) => s.mrf_id)).filter(Boolean))];

      transaction.set(prRef, {
        company_id,
        purchase_request_id: prRef.id,
        purchase_request_no: prNo,
        purchase_batch_id:   batchId,
        vendor_id:           g.vendor_id,
        vendor_name:         g.vendor_name || "",
        procurement_status:  "pending_vendor",
        status:              "pending",
        created_at:          serverTimestamp(),
        created_by,
        created_by_name:     created_by_name || "",
        total_items:         items.length,
        total_sites:         sites.length,
        total_mrfs:          mrfs.length,
        linked_site_names:   sites,
        source_mrf_ids:      mrfs,
        items,
      });

      for (const it of items) {
        for (const s of it.sources) {
          marks[s.mrf_id] = marks[s.mrf_id] || {};
          marks[s.mrf_id][s.item_index] = { pr_id: prRef.id, pr_no: prNo };
        }
      }
      created.push({ id: prRef.id, purchase_request_no: prNo, vendor_name: g.vendor_name || "" });
    });

    // One update per MRF, from the copy read INSIDE this transaction — every
    // marked line is kept (the old code kept only the last one).
    for (const [mrfId, byIndex] of Object.entries(marks)) {
      const d = mrfSnaps[mrfId];
      const items = (d.items || []).map((item, idx) => {
        const m = byIndex[idx];
        if (!m) return item;
        return {
          ...item,
          purchase_status:          "batched",          // legacy readers only — the queue ignores it now
          purchase_request_id:      m.pr_id,
          purchase_request_no:      m.pr_no,
          purchase_batch_id:        batchId,
          purchase_batched_at:      now,
        };
      });
      transaction.update(doc(db, "material_requests", mrfId), {
        items,
        updated_at: serverTimestamp(),
      });
    }

    transaction.set(counterRef, {
      last_number: last,
      updated_at:  serverTimestamp(),
    }, { merge: true });

    return created;
  });
};

/**
 * Cancels a PR that has not become a PO. Its quantity stops counting as
 * "already ordered", so the need shows again in the queue. Reason required.
 */
export const cancelPurchaseRequest = async ({
  purchase_request_id,
  company_id,
  reason,
  cancelled_by,
  cancelled_by_name,
  cancelled_by_role,
}) => {
  if (!purchase_request_id) throw new Error("purchase_request_id required");
  if (!company_id) throw new Error("company_id required");
  const why = String(reason || "").trim();
  if (!why) throw new Error("Reason is mandatory.");
  if (!MANAGER_ROLES.has(String(cancelled_by_role || "").toLowerCase())) {
    throw new Error("Only Owner / SM / Manager can cancel a purchase request.");
  }

  const ref = doc(db, "purchase_requests", purchase_request_id);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error("Purchase request not found");
    const d = snap.data();
    if (d.company_id !== company_id) throw new Error("Access denied — purchase request of another company");
    if (d.po_id || d.po_created || d.status === "po_created" || d.procurement_status === "po_generated") {
      throw new Error(`A PO (${d.po_number || d.po_id}) already exists for this request — cancel or short-close the PO instead.`);
    }
    if (d.status === "cancelled" || d.procurement_status === "cancelled") {
      throw new Error("This purchase request is already cancelled.");
    }
    transaction.update(ref, {
      status:              "cancelled",
      procurement_status:  "cancelled",
      cancel_reason:       why,
      cancelled_by:        cancelled_by || "",
      cancelled_by_name:   cancelled_by_name || "",
      cancelled_at:        serverTimestamp(),
      updated_at:          serverTimestamp(),
    });
  });
  return { success: true };
};